import { useTranslation } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useEffect, useState } from "react";

/**
 * The value a `url` question stores. Both halves are optional because `handleURLModalState`
 * seeds `answerValue` from `answer.value` with `answer` defaulting to `{}`, so an unanswered
 * field arrives here as `undefined` — which is exactly what the effect below copies into state.
 */
interface UrlFieldValue {
  URL?: string;
  displayText?: string;
}

interface AddURLModalProps {
  /** Forwarded verbatim to `handleSaveUrlData`; this modal never calls it itself. */
  handleCustomFieldData: unknown;
  /** Optional throughout because the declared default for this prop is a bare `{}`. */
  showUrlModal: {
    state?: boolean;
    uniqueId?: string | number;
    answerValue?: UrlFieldValue;
  };
  handleURLModalState: (params: { state: boolean }) => void;
  handleSaveUrlData: (handleCustomFieldData: unknown, urlData: UrlFieldValue) => void;
  name: string | number;
}

const AddURLModal = ({
  handleCustomFieldData,
  showUrlModal = {},
  handleURLModalState,
  handleSaveUrlData,
  name,
}: AddURLModalProps) => {
  const { t } = useTranslation();
  const [localState, setLocalState] = useState<UrlFieldValue>({
    URL: "",
    displayText: "",
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalState((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  useEffect(() => {
    setLocalState({
      URL: showUrlModal.answerValue?.URL,
      displayText: showUrlModal.answerValue?.displayText,
    });
  }, [showUrlModal.answerValue]);

  const modelContent = () => (
    <div className=" flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <span>{t("URL")}</span>
        <span>
          <TextFieldComponent
            name="URL"
            type="text"
            placeholder={t("Add URL")}
            showClearButton
            onChange={handleChange}
            value={localState?.URL}
          />
        </span>
      </div>
      <div className="flex flex-col gap-3">
        <span>{t("Display Text")}</span>
        <span>
          <TextFieldComponent
            name="displayText"
            type="text"
            placeholder={t("Add Display Text")}
            showClearButton
            onChange={handleChange}
            value={localState?.displayText}
          />
        </span>
      </div>
    </div>
  );

  return (
    <ModalStateDialogComponent
      headerTitle={<div className="flex justify-between w-full">{t("Add URL")}</div>}
      modelContent={modelContent()}
      width="50%"
      height="50%"
      onCancel={() => handleURLModalState({ state: false })}
      footerContent={
        <div className="flex gap-4 w-full">
          <ButtonComponent
            onClick={() => handleSaveUrlData(handleCustomFieldData, localState)}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          >
            {t("Save")}
          </ButtonComponent>
          <ButtonComponent
            onClick={() => handleURLModalState({ state: false })}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          >
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
      isVisible={showUrlModal.state && name == showUrlModal.uniqueId}
    />
  );
};
export default AddURLModal;
